import React, { useState, useContext, useMemo, useEffect } from "react";
import { Drawer, message, Checkbox, Select } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useMutation } from "@apollo/client";
import { UserSelectionDrawer } from "../../commonComponents/AssignmentDrawer";
import AssignmentMethodCard from "../../abandonedCalls/AssignmentMethodCard";
import CustomButton from "../../commonComponents/button";
import CustomLabel from "../../commonComponents/label";
import AssignUserIcon from "../../../icons/svg/assign-user.svg";
import RoundRobinIcon from "../../../icons/svg/assign-round-robin.svg";
import { REASSIGN_LEAD_OPPORTUNITY } from "../../../graphql/mutation";
import { UserContext } from "../../../amplify/authenticator-provider";

/**
 * Opportunity Assignment Drawer Component
 * Displays assignment methods for opportunity reassignment (Assign to User, Round Robin)
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Drawer visibility state
 * @param {Function} props.onClose - Close handler
 * @param {number} props.selectedCount - Number of selected opportunities
 * @param {Array} props.selectedOpportunities - Array of selected opportunity objects
 * @param {Function} props.onRefetch - Callback to refetch opportunities after assignment
 * @param {Object} props.sourceUser - Source user information
 * @param {Object} props.locationSetting - Location settings object containing round robin configuration
 */
const OpportunityAssignmentDrawer = ({
  open,
  onClose,
  selectedCount = 0,
  selectedOpportunities = [],
  onRefetch,
  sourceUser = null,
  locationSetting = null,
  currentLocationId = null,
  currentCompanyId = null,
}) => {
  const { user } = useContext(UserContext);
  const [assignToUserOpen, setAssignToUserOpen] = useState(false);
  const [roundRobinOpen, setRoundRobinOpen] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [reassignTasks, setReassignTasks] = useState(false);
  const [reassignAppointments, setReassignAppointments] = useState(false);
  const [selectedFromUserId, setSelectedFromUserId] = useState(null);

  const [reassignLeadOpportunity] = useMutation(REASSIGN_LEAD_OPPORTUNITY);

  // Extract unique users from selected opportunities with their userType
  const opportunityUsers = useMemo(() => {
    const usersMap = new Map();

    selectedOpportunities.forEach(opportunity => {
      const leadUsers = opportunity?.leadOpportunityUsers || [];
      leadUsers.forEach(leadUser => {
        if (leadUser?.user?.id) {
          const uniqueKey = `${leadUser.user.id}-${leadUser.userType?.name || ""}`;
          if (!usersMap.has(uniqueKey)) {
            usersMap.set(uniqueKey, {
              value: leadUser.user.id,
              uniqueKey,
              label: `${leadUser.user.fullName || "Unknown"}${
                leadUser.userType?.name ? ` (${leadUser.userType.name})` : ""
              }`,
              fullName: leadUser.user.fullName,
              userType: leadUser.userType?.name || null,
              userTypeId: leadUser.userType?.id || null,
            });
          }
        }
      });
    });

    return Array.from(usersMap.values()).sort((a, b) =>
      a.label.toLowerCase().localeCompare(b.label.toLowerCase())
    );
  }, [selectedOpportunities]);

  // Auto-select user: prioritize sourceUser if provided and exists in opportunityUsers,
  // otherwise auto-select if only one user exists
  useEffect(() => {
    if (sourceUser?.value) {
      // Check if sourceUser exists in opportunityUsers
      const matchingUser = opportunityUsers.find(
        u => parseInt(u.value) === parseInt(sourceUser.value)
      );
      if (matchingUser) {
        setSelectedFromUserId(matchingUser.value);
        return;
      }
    }
    // Fallback: auto-select if only one user exists
    if (opportunityUsers.length === 1) {
      setSelectedFromUserId(opportunityUsers[0].value);
    }
  }, [opportunityUsers, sourceUser]);

  // Filter opportunities that belong to the selectedFromUserId
  const getOpportunitiesForUser = userId => {
    if (!userId) return selectedOpportunities;
    return selectedOpportunities.filter(opportunity => {
      const leadUsers = opportunity?.leadOpportunityUsers || [];
      return leadUsers.some(leadUser => parseInt(leadUser?.user?.id) === parseInt(userId));
    });
  };

  // Get current user ID for reassignment (selectedFromUserId takes priority)
  const getCurrentUserId = () => {
    if (selectedFromUserId) return parseInt(selectedFromUserId);
    if (sourceUser?.value) return parseInt(sourceUser.value);
    return parseInt(user?.id);
  };

  // Check if "From User" selection is required (when there are multiple users)
  const isFromUserRequired = opportunityUsers.length > 0;
  const isFromUserValid = !isFromUserRequired || selectedFromUserId !== null;

  const handleAssignToUser = () => {
    if (!isFromUserValid) {
      message.error("Please select a user to reassign from");
      return;
    }
    setAssignToUserOpen(true);
  };

  const handleRoundRobin = () => {
    if (!isFromUserValid) {
      message.error("Please select a user to reassign from");
      return;
    }
    setRoundRobinOpen(true);
  };

  // Handle single user assignment (MANUAL_SELECT)
  const handleAssignToUserSubmit = async ({ userId, user: selectedUser }) => {
    setIsAssigning(true);
    try {
      const currentUserId = getCurrentUserId();

      const currentUserTypeId =
        opportunityUsers.find(u => parseInt(u.value) === parseInt(currentUserId))?.userTypeId ||
        null;
      const filteredOpportunities = getOpportunitiesForUser(selectedFromUserId);
      const leadOpportunityIds = filteredOpportunities.map(opp => parseInt(opp.id));

      const reassignmentGroups = [
        {
          currentUserId,
          currentUserTypeId,
          leadOpportunityIds,
        },
      ];

      const payload = {
        reassignmentGroups,
        assignmentType: "MANUAL_SELECT",
        assignedUserIds: [parseInt(userId)],
        isTaskAssignment: reassignTasks,
        isAppointmentAssignment: reassignAppointments,
      };
      console.log("Submitting manual assignment with payload:", payload);
      const response = await reassignLeadOpportunity({
        variables: payload,
      });

      if (response?.data?.reassignLeadOpportunity?.statusCode === 200) {
        message.success(
          response?.data?.reassignLeadOpportunity?.message ||
            `Successfully assigned ${filteredOpportunities.length} ${
              filteredOpportunities.length === 1 ? "opportunity" : "opportunities"
            } to ${selectedUser?.fullName || "selected user"}`
        );
        setAssignToUserOpen(false);
        onClose();
        onRefetch?.();
      } else {
        message.error(
          response?.data?.reassignLeadOpportunity?.message || "Failed to assign opportunities"
        );
      }
    } catch (error) {
      console.error("Error in assignment:", error);
      message.error("An error occurred while assigning opportunities");
    } finally {
      setIsAssigning(false);
    }
  };

  // Handle round robin assignment (ROUND_ROBIN)
  const handleRoundRobinSubmit = async ({ userIds, users }) => {
    setIsAssigning(true);
    try {
      const currentUserId = getCurrentUserId();

      const currentUserTypeId =
        opportunityUsers.find(u => parseInt(u.value) === parseInt(currentUserId))?.userTypeId ||
        null;

      const filteredOpportunities = getOpportunitiesForUser(selectedFromUserId);
      const leadOpportunityIds = filteredOpportunities.map(opp => parseInt(opp.id));
      const assignedUserIds = userIds.map(id => parseInt(id));

      const reassignmentGroups = [
        {
          currentUserId,
          currentUserTypeId,
          leadOpportunityIds,
        },
      ];
      const payload = {
        reassignmentGroups,
        assignmentType: "ROUND_ROBIN",
        assignedUserIds,
        isTaskAssignment: reassignTasks,
        isAppointmentAssignment: reassignAppointments,
      };
      console.log("Submitting round-robin assignment with payload:", payload);
      const response = await reassignLeadOpportunity({
        variables: payload,
      });

      if (response?.data?.reassignLeadOpportunity?.statusCode === 200) {
        message.success(
          response?.data?.reassignLeadOpportunity?.message ||
            `Successfully assigned ${filteredOpportunities.length} ${
              filteredOpportunities.length === 1 ? "opportunity" : "opportunities"
            } using round-robin to ${users.length} users`
        );
        setRoundRobinOpen(false);
        onClose();
        onRefetch?.();
      } else {
        message.error(
          response?.data?.reassignLeadOpportunity?.message || "Failed to assign opportunities"
        );
      }
    } catch (error) {
      console.error("Error in round-robin assignment:", error);
      message.error("An error occurred while assigning opportunities");
    } finally {
      setIsAssigning(false);
    }
  };

  /**
   * Extract and merge all included user IDs from round robin algorithm settings
   * @param {Object} locationSetting - Location settings object
   * @returns {Array} Array of user IDs to be preselected
   */
  const getPreselectedUserIds = locationSetting => {
    // Add more specific checks
    if (!locationSetting) {
      return [];
    }

    if (!locationSetting.ROUND_ROBIN_ALGORITHM) {
      return [];
    }

    if (!locationSetting.ROUND_ROBIN_ALGORITHM.assignee) {
      return [];
    }

    const assigneeConfig = locationSetting.ROUND_ROBIN_ALGORITHM.assignee;
    const allUserIds = [];

    // Get user IDs from teams
    if (assigneeConfig.teams && Array.isArray(assigneeConfig.teams)) {
      assigneeConfig.teams.forEach(team => {
        if (team.included_user_ids && Array.isArray(team.included_user_ids)) {
          allUserIds.push(...team.included_user_ids);
        }
      });
    }

    // Get user IDs from users object
    if (
      assigneeConfig.users?.included_user_ids &&
      Array.isArray(assigneeConfig.users.included_user_ids)
    ) {
      allUserIds.push(...assigneeConfig.users.included_user_ids);
    }

    // Get user IDs from company roles
    if (assigneeConfig.company_roles && Array.isArray(assigneeConfig.company_roles)) {
      assigneeConfig.company_roles.forEach(role => {
        if (role.included_user_ids && Array.isArray(role.included_user_ids)) {
          allUserIds.push(...role.included_user_ids);
        }
      });
    }

    // Remove duplicates and convert to numbers
    const result = [...new Set(allUserIds.map(id => parseInt(id)))];

    return result;
  };

  return (
    <>
      <Drawer
        title={
          <div>
            <div className="text-base font-semibold text-black-88">Re-assignment</div>
            <div className="text-xs font-normal text-black-65">
              {selectedCount} {selectedCount === 1 ? "opportunity" : "opportunities"} selected
            </div>
          </div>
        }
        placement="right"
        onClose={onClose}
        open={open}
        width={600}
        footer={
          <div className="flex justify-start">
            <CustomButton btnText="Cancel" handleClick={onClose} />
          </div>
        }
        closeIcon={<CloseOutlined className="icon-color" />}
      >
        {/* From User Selection */}
        {opportunityUsers.length > 0 && (
          <div className="mb-6">
            <CustomLabel
              label={
                <>
                  From User <span className="text-red-500">*</span>
                </>
              }
              labelClass="text-xs font-semibold text-black-65 mb-2"
            />
            <Select
              className="w-full"
              placeholder="Select user to reassign from"
              value={selectedFromUserId}
              onChange={value => setSelectedFromUserId(value)}
              allowClear={opportunityUsers.length > 1}
              showSearch
              optionFilterProp="label"
              status={!isFromUserValid ? "error" : undefined}
              options={opportunityUsers.map(u => ({
                value: u.value,
                label: u.label,
              }))}
            />
            <p className="text-xs text-gray-500 mt-1">
              Select the current owner whose opportunities you want to reassign
            </p>
          </div>
        )}

        {/* Reassignment Options */}
        <div className="mb-6">
          <h3 className="text-xs font-semibold text-black-65 mb-3 tracking-wider">
            Reassignment options
          </h3>
          <div className="space-y-2">
            <Checkbox checked={reassignTasks} onChange={e => setReassignTasks(e.target.checked)}>
              Re-assign tasks
            </Checkbox>
            <br />
            <Checkbox
              checked={reassignAppointments}
              onChange={e => setReassignAppointments(e.target.checked)}
            >
              Re-assign appointments
            </Checkbox>
          </div>
        </div>

        {/* Direct Assignment Section */}
        <div className="mb-6">
          <h3 className="text-xs font-semibold text-black-65 mb-3 tracking-wider">
            Assignment methods
          </h3>
          <div className="space-y-3">
            <AssignmentMethodCard
              icon={AssignUserIcon}
              title="Assign to User"
              description="Assign all selected opportunities to a specific user"
              onClick={handleAssignToUser}
            />
            <AssignmentMethodCard
              icon={RoundRobinIcon}
              title="Round Robin"
              description="Distribute opportunities evenly among selected users"
              onClick={handleRoundRobin}
            />
          </div>
        </div>
      </Drawer>

      {/* Nested Drawers - Using Generic UserSelectionDrawer */}
      {assignToUserOpen && (
        <UserSelectionDrawer
          open={assignToUserOpen}
          onClose={() => setAssignToUserOpen(false)}
          onAssign={handleAssignToUserSubmit}
          loading={isAssigning}
          title="Assign to User"
          selectLabel="Select a user to assign opportunities"
          searchPlaceholder="Search users..."
          assignButtonText="Assign"
          cancelButtonText="Cancel"
          validationMessage="Please select a user"
          selectionType="radio"
          showSelectAll={false}
          userLocationId={currentLocationId}
          currentCompanyId={currentCompanyId}
          fetchOnlyActiveUsers
        />
      )}

      {roundRobinOpen && (
        <UserSelectionDrawer
          open={roundRobinOpen}
          onClose={() => setRoundRobinOpen(false)}
          onAssign={handleRoundRobinSubmit}
          loading={isAssigning}
          title="Round Robin Assignment"
          selectLabel="Select users for round-robin assignment"
          searchPlaceholder="Search users..."
          assignButtonText="Assign"
          cancelButtonText="Cancel"
          validationMessage="Please select at least one user"
          selectionType="checkbox"
          showSelectAll
          selectAllText="Select all users"
          infoMessage="Selected opportunities will be distributed evenly among the chosen users in a round-robin fashion."
          preselectedUserIds={getPreselectedUserIds(locationSetting)}
          userLocationId={currentLocationId}
          currentCompanyId={currentCompanyId}
          fetchOnlyActiveUsers
        />
      )}
    </>
  );
};

export default OpportunityAssignmentDrawer;
